/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 解決済み ops 依頼の「自動レポート」ボックス。ステータス・分類・バグチケット・起票者に加え、解決時に承認された対応結論（closeReport）があれば全文表示する。依頼詳細のメインカラムに出す
 * @graph-connects none
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AccentBox } from './OpsPanels.js';
import { ClassificationBadge, InfoRow } from './OpsPrimitives.js';
import type { OpsRequest } from '../../../types/ops.js';

/** OpsClosedReportBox の props。 */
export interface OpsClosedReportBoxProps {
  /** 表示対象の解決済み依頼。 */
  request: OpsRequest;
}

/**
 * 解決済み依頼の自動レポートボックス。承認済みの対応結論（closeReport）は複数行になりうるため全文（whitespace 保持）で出す。
 * @graph-connects none
 */
export function OpsClosedReportBox({ request }: OpsClosedReportBoxProps): ReactNode {
  const { t } = useTranslation();
  return (
    <AccentBox tone="success" title={t('ops.detail.autoReportTitle')}>
      <div className="space-y-1">
        <InfoRow label={t('ops.detail.autoReport.status')}>
          {t('ops.detail.autoReport.resolved')}
        </InfoRow>
        <InfoRow label={t('ops.detail.autoReport.classification')}>
          <ClassificationBadge classification={request.classification} />
        </InfoRow>
        {request.backlogTicketUrl !== null && (
          <InfoRow label={t('ops.detail.autoReport.bugTicket')}>
            <a
              href={request.backlogTicketUrl}
              className="text-[var(--color-accent-primary)] hover:underline"
            >
              {request.backlogTicketUrl.split('/').pop()}
            </a>
          </InfoRow>
        )}
        <InfoRow label={t('ops.detail.autoReport.requester')}>
          {t('ops.detail.autoReport.requesterValue', { requester: request.requesterName })}
        </InfoRow>
        {request.closeReport !== null && request.closeReport !== '' && (
          <div className="pt-1">
            <div className="text-xs text-[var(--color-text-muted)]">
              {t('ops.detail.autoReport.report')}
            </div>
            <p className="mt-0.5 whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
              {request.closeReport}
            </p>
          </div>
        )}
      </div>
    </AccentBox>
  );
}
